import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Draw from 'ol/interaction/Draw';

import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import CircleStyle from 'ol/style/Circle';
import Text from 'ol/style/Text';

import type Map from 'ol/Map';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';

export class MeasureTool {
  geometryType: 'LineString' | 'Polygon' = 'LineString';
  private resultElement: HTMLElement | null;
  private measuringTool?: Draw;
  private map: Map;

  private source = new VectorSource();

  private vectorLayer = new VectorLayer({
    source: this.source,
    style: (feature: Feature<Geometry>) => {
      return new Style({
        fill: new Fill({ color: 'rgba(255, 255, 255, 0.8)' }),
        stroke: new Stroke({ color: '#ffcc33', width: 2 }),
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({ color: '#ffcc33' }),
        }),
        text: new Text({
          font: '16px sans-serif',
          text: String(feature.get('labeltext') ?? ''),
          offsetY: -10,
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({ color: '#fff', width: 4 }),
        }),
      });
    },
  });

  constructor(map: Map, resultElementId: string = 'js-result') {
    this.map = map;
    this.resultElement = document.getElementById(resultElementId);
    this.map.addLayer(this.vectorLayer);
  }

  enable(mode: 'length' | 'area'): void {
    this.enableMeasuringTool(mode);
  }

  disable(): void {
    if (this.measuringTool) this.map.removeInteraction(this.measuringTool);
    this.vectorLayer.getSource().clear();
    if (this.resultElement) this.resultElement.innerHTML = '';
  }

  private enableMeasuringTool(mode: 'length' | 'area'): void {
    this.geometryType = mode === 'area' ? 'Polygon' : 'LineString';

    if (this.measuringTool) this.map.removeInteraction(this.measuringTool);

    const htmlSuffix = this.geometryType === 'Polygon' ? '<sup>2</sup>' : '';

    this.measuringTool = new Draw({
      type: this.geometryType,
      source: this.vectorLayer.getSource()!,
    });

    this.measuringTool.on('drawstart', (e) => {
      this.vectorLayer.getSource()!.clear();

      const feature = e.feature as Feature<Geometry>;

      feature.on('change', (event: any) => {
        const geom = event.target.getGeometry();
        if (!geom) return;

        // NOTE: getArea/getLength exist on Polygon/LineString respectively
        const measurement =
          this.geometryType === 'Polygon'
            ? (geom as any).getArea?.()
            : (geom as any).getLength?.();

        if (typeof measurement !== 'number') return;

        const measurementFormatted =
          this.geometryType === 'Polygon'
            ? `${measurement.toFixed(2)}m${htmlSuffix}`
            : `${measurement.toFixed(1)}m`;

        if (this.resultElement) this.resultElement.innerHTML = measurementFormatted;
        feature.set('labeltext', measurementFormatted.replace(/<[^>]*>/g, '')); // store plain text label
      });
    });

    this.map.addInteraction(this.measuringTool);
  }
}
